import axios, { AxiosInstance } from 'axios';
import { useMemo } from 'react';
import { useGetAccessToken } from '@/hooks/useGetAccessToken';
import { getAppConstants } from '@/lib/getAppConstants';
import { isLocal } from '@/lib/isLocal';
import {
  Configuration,
  EventDokumentTypMappingsApi,
  VorgngeApi,
} from './__generated';

/**
 * Returns configured api url with an optional suffix.
 *
 * Preserves local development and ignores stage, when isLocal() === true
 *
 * @param local
 */
const apiBaseUrl = (local: boolean = isLocal()): string => {
  const url =
    local === true
      ? '/api' // route to internal /api to mitigate CORS-Issues See: /components/opm-cockpit-frontend/frontend/vite.config.base.ts:57
      : getAppConstants().OPM_COCKPIT_API_URL;

  // remove tailing slash and append with suffix
  return url.replace(/\/+$/, '');
};

interface ApiClients {
  vorgaengeApi: VorgngeApi;
  eventDokumentTypMappingsApi: EventDokumentTypMappingsApi;
  axiosInstance: AxiosInstance;
}

export const useApiClient = (): ApiClients => {
  const baseURL = apiBaseUrl();
  const canDebug = import.meta.env.DEV || isLocal();
  const getAccessToken = useGetAccessToken();
  return useMemo(() => {
    const axiosInstance = axios.create({
      baseURL,
      headers: {
        'Content-Type': 'application/json',
      },
      timeout: 60_000,
    });

    /**
     * Attach accessToken to Authorization header when not local
     */
    axiosInstance.interceptors.request.use(
      async (config) => {
        const accessToken = await getAccessToken();
        config.headers.Authorization = `Bearer ${accessToken}`;
        return config;
      },
      (error) => Promise.reject(error),
      { runWhen: () => !isLocal() },
    );

    /**
     * Log requests when in local environment or on dev.
     */
    axiosInstance.interceptors.request.use(
      (config) => {
        // eslint-disable-next-line no-console
        console.log(
          `API Request: ${config.method?.toUpperCase()} ${config.url}`,
        );
        return config;
      },
      (error) => {
        // eslint-disable-next-line no-console
        console.error('API Request Error:', error);
        return Promise.reject(error);
      },
      { runWhen: () => canDebug },
    );

    /**
     * Log response when in local environment or on dev.
     */
    axiosInstance.interceptors.response.use(
      (response) => response,
      (error) => {
        if (canDebug) {
          // eslint-disable-next-line no-console
          console.error(
            'API Response Error:',
            error.response?.data || error.message,
          );
        }
        return Promise.reject(error);
      },
    );

    const configuration = new Configuration({
      basePath: '',
      baseOptions: {
        timeout: 60_000,
      },
    });

    const vorgaengeApi = new VorgngeApi(configuration, '', axiosInstance);
    const eventDokumentTypMappingsApi = new EventDokumentTypMappingsApi(
      configuration,
      '',
      axiosInstance,
    );

    return {
      axiosInstance,
      vorgaengeApi,
      eventDokumentTypMappingsApi,
    };
  }, []);
};
